import { useEffect, useRef, useState } from "react";
import { getStoredToken } from "../../utils/impersonation.utils";
import type { PreVisitBriefData } from "./widgets/PreVisitBrief";

// Pre-charting REST calls (backend `prechart/`, docs/precharting/api-contract.md)
// and the on-open trigger that posts the pre-visit brief into the Superdoc
// thread. Every failure here is silent: the brief is a bonus on top of the
// chat, never something that may break or slow it.

export type PrechartStatus =
  | "disabled"
  | "none"
  | "queued"
  | "collecting"
  | "extracting"
  | "synthesizing"
  | "ready"
  | "partial"
  | "failed";

export interface PrechartBriefResponse {
  enabled: boolean;
  status: PrechartStatus;
  run_id?: string | null;
  brief?: PreVisitBriefData | null;
  shown_in_thread?: boolean;
}

export type PrechartRefreshResult = "queued" | "in_progress" | "disabled" | "error";

export type PreVisitBriefAction = { type: "pre_visit_brief"; payload: { brief_id: string } };

export const prechartBaseUrl = (): string => process.env.REACT_APP_SERVER_URL || "";

// The same headers the Superdoc runtime sends on its own REST calls.
export const prechartHeaders = (clientId?: number): Record<string, string> => {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getStoredToken();
  if (token) headers["Authorization"] = `Token ${token}`;
  if (clientId) headers["X-Client-ID"] = clientId.toString();
  return headers;
};

export async function fetchPrechartBrief(baseUrl: string, clientId: number): Promise<PrechartBriefResponse | null> {
  const res = await fetch(`${baseUrl}/prechart/brief/?client_id=${encodeURIComponent(String(clientId))}`, {
    headers: prechartHeaders(clientId),
  });
  if (!res.ok) return null;
  return (await res.json()) as PrechartBriefResponse;
}

export async function requestPrechartRefresh(baseUrl: string, clientId: number): Promise<PrechartRefreshResult> {
  try {
    const res = await fetch(`${baseUrl}/prechart/refresh/`, {
      method: "POST",
      headers: prechartHeaders(clientId),
      body: JSON.stringify({ client_id: clientId }),
    });
    if (res.ok) return "queued";
    if (res.status === 409) return "in_progress";
    if (res.status === 403) return "disabled";
    return "error";
  } catch (err) {
    console.debug("[Superdoc] pre-chart refresh failed", err);
    return "error";
  }
}

export async function sendPrechartFeedback(
  baseUrl: string,
  briefId: string,
  helpful: boolean,
  clientId?: number,
): Promise<boolean> {
  try {
    const res = await fetch(`${baseUrl}/prechart/brief/${encodeURIComponent(briefId)}/feedback/`, {
      method: "POST",
      headers: prechartHeaders(clientId),
      body: JSON.stringify({ helpful, comment: "" }),
    });
    return res.ok;
  } catch (err) {
    console.debug("[Superdoc] pre-chart feedback failed", err);
    return false;
  }
}

// ── Originals: a document's stored file, or the prior note, in a new tab ──

export interface OpenOriginalOptions {
  // What the tab says while it loads ("Consult letter"); never the patient.
  title: string;
  // The document may be another patient's: the tab asks before it loads.
  checkPatient?: boolean;
  clientId?: number;
}

export type OpenOriginalResult = "opened" | "blocked";

// How long a document's blob URL outlives the tab's navigation to it.
const BLOB_URL_TTL_MS = 60_000;

const TAB_CSS = `
  body { margin: 0; font: 14px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, sans-serif; color: #374151; background: #F7F8FA; }
  main { max-width: 760px; margin: 0 auto; padding: 40px 24px 64px; }
  h1 { font-size: 18px; font-weight: 600; color: #111827; margin: 0 0 4px; }
  main > p { margin: 0 0 16px; color: #6B7280; }
  .warn { color: #92400E; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 10px; padding: 12px 14px; margin: 0 0 16px; }
  button { font: inherit; font-weight: 500; padding: 7px 14px; border-radius: 8px; border: 1px solid #D6D9E0; background: #FFF; color: #111827; cursor: pointer; }
  article { background: #FFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 8px 32px 28px; color: #1F2937; font-size: 14.5px; line-height: 1.6; overflow-wrap: anywhere; }
  article h2 { font-size: 12px; font-weight: 600; color: #4B5563; text-transform: uppercase; letter-spacing: 0.05em; margin: 22px 0 8px; padding-top: 16px; border-top: 1px solid #EEF0F3; }
  article h2:first-child { border-top: 0; padding-top: 8px; }
  article h3 { font-size: 14.5px; font-weight: 600; color: #111827; margin: 12px 0 2px; }
  article p { margin: 0 0 6px; }
  article .item { padding-left: 22px; text-indent: -22px; margin: 0 0 8px; }
  article .head { color: #6B7280; font-size: 13.5px; margin: 0 0 2px; }
  @media print { body { background: #FFF; } article { border: 0; padding: 0; } }
`;

// A note's text as blocks to set as a document: the EHR's printed notes put
// each section name ("Current Medications", "Assessments") and each label
// ("Cardiovascular:") on a line of its own, with no blank lines between.
export type NoteBlock = { kind: "section" | "label" | "item" | "text"; text: string };

// Section names eCW prints, and the sections of a Nudge note; matched whole,
// case-insensitive, with or without a trailing colon.
const NOTE_SECTIONS = new Set(
  [
    "reason for appointment",
    "chief complaint",
    "history of present illness",
    "progress notes",
    "current medications",
    "medications",
    "past medical history",
    "surgical history",
    "family history",
    "social history",
    "allergies",
    "hospitalization/major diagnostic procedure",
    "review of systems",
    "vital signs",
    "examination",
    "physical examination",
    "physical exam",
    "assessments",
    "assessment",
    "assessment and plan",
    "treatment",
    "plan",
    "procedures",
    "procedure codes",
    "immunizations",
    "therapeutic injections",
    "diagnostic imaging",
    "lab reports",
    "labs",
    "preventive medicine",
    "counseling",
    "follow up",
    "care plan details",
    "visit code",
    "problem list",
    "instructions",
    "referrals",
    "subjective",
    "objective",
    "data",
    "hpi",
  ].map((name) => name.toLowerCase()),
);

export const noteBlocks = (text: string): NoteBlock[] => {
  const blocks: NoteBlock[] = [];
  for (const raw of text.split(/\r?\n/)) {
    // A markdown heading or a whole line in bold reads as a heading too.
    let line = raw.trim().replace(/^#{1,4}\s+/, "");
    const markdownHeading = line !== raw.trim();
    const bold = /^\*\*(.+)\*\*:?$/.exec(line);
    if (bold) line = bold[1].trim();
    if (!line) continue;
    const name = line.replace(/\s*:$/, "").toLowerCase();
    if (NOTE_SECTIONS.has(name) || markdownHeading) {
      blocks.push({ kind: "section", text: line.replace(/\s*:$/, "") });
    } else if (bold || (line.length <= 48 && /:$/.test(line) && !/\d{1,2}\/\d{1,2}/.test(line))) {
      blocks.push({ kind: "label", text: line.replace(/\s*:$/, "") });
    } else if (/^\d{1,2}[.)]\s/.test(line)) {
      blocks.push({ kind: "item", text: line });
    } else if (line.length <= 32 && /^[A-Z][A-Za-z/&()' -]*$/.test(line)) {
      // "Taking", "Not-Taking", "Discontinued": a group's name, not a sentence.
      blocks.push({ kind: "label", text: line });
    } else {
      blocks.push({ kind: "text", text: line });
    }
  }
  // A numbered line with its own lines under it heads them ("1. Hypertension"
  // over its orders and notes in a Plan); a run of numbered lines is a list.
  return blocks.map((b, i) =>
    b.kind === "item" && blocks[i + 1]?.kind === "text" ? { kind: "label", text: b.text } : b,
  );
};

// Replaces the opened tab's page with a heading, an optional line, an
// optional warning, an optional button and an optional text block. Built
// with DOM nodes and textContent: nothing from the chart is parsed as HTML.
const paintTab = (
  tab: Window,
  page: { title: string; line?: string; warn?: string; button?: [string, () => void]; note?: string },
) => {
  const doc = tab.document;
  doc.head.replaceChildren();
  doc.title = `${page.title} · Nudge`;
  const style = doc.createElement("style");
  style.textContent = TAB_CSS;
  doc.head.appendChild(style);
  const main = doc.createElement("main");
  const h1 = doc.createElement("h1");
  h1.textContent = page.title;
  main.appendChild(h1);
  if (page.line) {
    const p = doc.createElement("p");
    p.textContent = page.line;
    main.appendChild(p);
  }
  if (page.warn) {
    const warn = doc.createElement("div");
    warn.className = "warn";
    warn.textContent = page.warn;
    main.appendChild(warn);
  }
  if (page.button) {
    const [label, onClick] = page.button;
    const button = doc.createElement("button");
    button.type = "button";
    button.textContent = label;
    button.addEventListener("click", onClick);
    main.appendChild(button);
  }
  if (page.note != null) {
    const article = doc.createElement("article");
    // The lines before the first section are the note's own header (patient,
    // visit, provider): set quietly.
    let inHeader = true;
    for (const block of noteBlocks(page.note)) {
      if (block.kind === "section") inHeader = false;
      const el = doc.createElement(block.kind === "section" ? "h2" : block.kind === "label" ? "h3" : "p");
      if (block.kind === "item") el.className = "item";
      else if (inHeader && block.kind === "text") el.className = "head";
      el.textContent = block.text;
      article.appendChild(el);
    }
    main.appendChild(article);
  }
  doc.body.replaceChildren(main);
};

/**
 * Opens a brief's original (`doc:<id>` file or `note:<id>` text) in a new tab.
 * Call it from the click itself: the tab opens synchronously (no popup
 * blocker), says "Opening…", then shows the file (a `blob:` URL, so no
 * patient data or token ever sits in a URL) or the note's text. A document
 * that may be another patient's waits behind "Open anyway".
 */
export function openPrechartOriginal(
  baseUrl: string,
  briefId: string,
  ref: string,
  { title, checkPatient, clientId }: OpenOriginalOptions,
): OpenOriginalResult {
  const tab = window.open("", "_blank");
  if (!tab) return "blocked";
  // The tab keeps no handle on the app; the app keeps its handle on the tab.
  tab.opener = null;
  const load = async () => {
    paintTab(tab, { title, line: "Opening…" });
    try {
      const res = await fetch(`${baseUrl}/prechart/brief/${encodeURIComponent(briefId)}/original/`, {
        method: "POST",
        headers: prechartHeaders(clientId),
        body: JSON.stringify({ ref }),
      });
      if (!res.ok) throw new Error(`original ${res.status}`);
      if ((res.headers.get("Content-Type") || "").includes("application/json")) {
        const note = (await res.json()) as { title?: string; text?: string; source?: string };
        const line =
          note.source === "ehr_import" ? "Imported from your EHR · read-only" : "Your Nudge note · read-only";
        paintTab(tab, { title: note.title || title, line, note: note.text || "" });
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      tab.location.replace(url);
      setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_TTL_MS);
    } catch (err) {
      console.debug("[Superdoc] pre-chart original failed", err);
      if (tab.closed) return;
      paintTab(tab, { title, line: "Couldn't open this document.", button: ["Try again", load] });
    }
  };
  if (checkPatient) {
    paintTab(tab, {
      title,
      warn: "This document may belong to another patient: the name or birth date on it doesn't match this chart.",
      button: ["Open anyway", load],
    });
  } else {
    load();
  }
  return "opened";
}

export interface PrechartLabValues {
  ref: string;
  values: NonNullable<PreVisitBriefData["labs"]>[number]["values"];
  value_total?: number | null;
  flagged_total?: number | null;
  unflagged_total?: number | null;
}

// Every value of a lab panel the brief cut at 25; null when it can't be had.
export async function fetchPrechartLabValues(
  baseUrl: string,
  briefId: string,
  ref: string,
  clientId?: number,
): Promise<PrechartLabValues | null> {
  try {
    const res = await fetch(
      `${baseUrl}/prechart/brief/${encodeURIComponent(briefId)}/lab/?ref=${encodeURIComponent(ref)}`,
      { headers: prechartHeaders(clientId) },
    );
    if (!res.ok) return null;
    return (await res.json()) as PrechartLabValues;
  } catch (err) {
    console.debug("[Superdoc] pre-chart lab values failed", err);
    return null;
  }
}

/**
 * The brief to post into the thread, or null: only a finished brief
 * (ready / partial) for a clinician who has pre-charting on, and only when the
 * thread doesn't already carry this version.
 */
export const briefIdToAnnounce = (res: PrechartBriefResponse | null | undefined): string | null => {
  if (!res?.enabled) return null;
  if (res.status !== "ready" && res.status !== "partial") return null;
  if (res.shown_in_thread) return null;
  return res.brief?.brief_id || null;
};

interface UsePreVisitBriefTriggerOptions {
  baseUrl: string;
  clientId?: number;
  // The thread's own history has landed. Firing earlier would stream the
  // brief into the thread and then have the history load overwrite it.
  ready: boolean;
  fire: (action: PreVisitBriefAction) => unknown;
}

/**
 * On Superdoc mount for a patient: ask once whether a pre-visit brief is
 * waiting and, if so, fire the `pre_visit_brief` custom action once. The panel
 * remounts per patient (`key={activeClientId}`), so "once" is per mount; the
 * lookup runs in parallel with everything else and never holds up the
 * post-encounter review trigger.
 */
export function usePreVisitBriefTrigger({ baseUrl, clientId, ready, fire }: UsePreVisitBriefTriggerOptions) {
  const fireRef = useRef(fire);
  fireRef.current = fire;
  const mountedRef = useRef(false);
  const requestedForRef = useRef<number | null>(null);
  const firedRef = useRef<Set<string>>(new Set());
  const [pendingBriefId, setPendingBriefId] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // One lookup per patient per mount — also under StrictMode's re-run.
    if (!clientId || requestedForRef.current === clientId) return;
    requestedForRef.current = clientId;
    fetchPrechartBrief(baseUrl, clientId)
      .then((res) => {
        const briefId = briefIdToAnnounce(res);
        if (briefId && mountedRef.current) setPendingBriefId(briefId);
      })
      .catch((err) => console.debug("[Superdoc] pre-visit brief lookup failed", err));
  }, [baseUrl, clientId]);

  useEffect(() => {
    if (!ready || !pendingBriefId || firedRef.current.has(pendingBriefId)) return;
    firedRef.current.add(pendingBriefId);
    try {
      Promise.resolve(fireRef.current({ type: "pre_visit_brief", payload: { brief_id: pendingBriefId } })).catch(
        (err) => console.debug("[Superdoc] pre-visit brief action failed", err),
      );
    } catch (err) {
      console.debug("[Superdoc] pre-visit brief action failed", err);
    }
  }, [ready, pendingBriefId]);
}
